<template>
  <b-modal
    :id="`field-calculation-modal-${index}`"
    size="xl"
    title="Edit Field Calculation"
    @show="onShow"
    @hide="onHide"
  >
    <FieldDefinitions
      :name.sync="editFieldCalculation.name"
      :definitions.sync="editFieldCalculation.definitions"
      :unit.sync="editFieldCalculation.unit"
      :is-monetary.sync="editFieldCalculation.isMonetary"
    />
    <b-alert
      v-for="(msg, msgI) in warnings"
      :key="`field-definitions-modal-warning-${msgI}`"
      dismissible
      variant="warning"
      show
      @dismissed="warnings.splice(msgI, 1)"
    >
      {{ msg }}
    </b-alert>
  </b-modal>
</template>

<script lang="ts">

import Vue, { PropType } from 'vue'
import Component from 'vue-class-component'

import FieldDefinitions from '@/components/builder/FieldCalculator/FieldDefinitions.vue'
import { helpers, required } from 'vuelidate/lib/validators'
import { parse } from '@/store/app/math'
import { BvModalEvent } from 'bootstrap-vue'
import { FieldCalculationDefinition } from '@/types'

const cloneDeep = require('lodash.clonedeep')

const components = {
  FieldDefinitions,
}

const props = {
  isNewField: {
    type: Boolean,
    default: false,
  },
  index: {
    type: Number,
    required: true,
  },
  name: {
    type: String,
    required: true,
  },
  definitions: {
    type: Array as PropType<FieldCalculationDefinition[]>,
    required: true,
  },
  unit: {
    type: String,
    required: true,
  },
  isMonetary: {
    type: Boolean,
    required: true,
  },
}

@Component({ components })
export default class FieldDefinitionsModal extends Vue.extend({ props }) {
  editFieldCalculation = {
    name: '',
    definitions: [],
    unit: '',
    isMonetary: false,
  }

  warnings: any[] = []

  validations () {
    return {
      editFieldCalculation: {
        name: {
          required,
        },
        definitions: {
          required,
        },
        unit: {
          optional: (value: any) => !helpers.req(value) || value.trim().length > 0,
        },
      },
    }
  }

  get stringifyDefinitions () {
    return this.$typedStore.primary.entities.fieldCalculation.stringifyDefinitions
  }

  get numericMonetaryFields () {
    return this.$typedStore.visualisationApp.numericMonetaryFields
  }

  get combinedScope () {
    return this.$typedStore.primary.entities.fieldCalculation.combinedScope
  }

  onShow () {
    const fieldCalculation = {
      name: this.name,
      definitions: this.definitions,
      unit: this.unit,
      isMonetary: this.isMonetary,
    }
    this.editFieldCalculation = cloneDeep(fieldCalculation)
    this.warnings = []
  }

  onHide (bvModalEvt: BvModalEvent) {
    if (bvModalEvt.trigger === 'ok') {
      // on ok, therefore updating values, check values
      this.$v.$touch()
      this.warnings = [] // resets warnings
      this.validateFieldDefinitions(bvModalEvt)
      try {
        const parsedDefinitions = parse(this.stringifyDefinitions(this.editFieldCalculation.definitions))
        parsedDefinitions.compile().evaluate(this.combinedScope()) // exist to trigger the error catching
        if (!this.$v.$invalid) {
          if (parsedDefinitions) {
            this.$emit('update:name', this.editFieldCalculation.name)
            this.$emit('update:definitions', this.editFieldCalculation.definitions)
            this.$emit('update:unit', this.editFieldCalculation.unit)
            this.$emit('update:isMonetary', this.editFieldCalculation.isMonetary)
            this.$nextTick(() => {
              this.$bvModal.hide(`field-calculation-modal-${this.index}`)
            })
          } else {
            // This technically shouldn't reach this point as the error catching should jump out of the logic
            // The only way this could happen if for some reason parsedDefinitions is undefined or similar but no error thrown by math js
            bvModalEvt.preventDefault()
            this.warnings.push('Definitions are invalid')
          }
        }
      } catch (err) {
        bvModalEvt.preventDefault()
        this.warnings.push(err)
      }
    } else {
      // on close click, cancel click, backdrop click, esc pressed
      // therefore don't update values, don't check
      if (this.isNewField) {
        this.$emit('cancel-new-field-defs')
      }
    }
  }

  validateFieldDefinitions (bvModalEvt: BvModalEvent) {
    if (this.$v.$invalid) {
      bvModalEvt.preventDefault()
      if (this.$v.editFieldCalculation?.name?.$invalid) {
        this.warnings.push('Requires a name')
      }
      if (this.$v.editFieldCalculation?.definitions?.$invalid) {
        this.warnings.push('Require definitions')
      }
      if (this.$v.editFieldCalculation?.unit?.$invalid) {
        this.warnings.push('Unit is invalid')
      }
    }
  }
}

</script>
